import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { useSEO, getOrganizationSchema, getLocalBusinessSchema, getBreadcrumbSchema } from '../../utils/seo';

export default function Contact() {
  // SEO Configuration
  useSEO({
    title: 'Contact Us - The Villarreal Health Network',
    description: 'Contact The Villarreal Health Network for home healthcare services in Las Vegas. Multiple office locations, 24/7 emergency support, and expert care coordination available.',
    keywords: 'contact healthcare provider, Las Vegas home health, healthcare contact information, care coordination, emergency healthcare support',
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        getOrganizationSchema(),
        getLocalBusinessSchema(),
        {
          "@type": "ContactPage",
          "name": "Contact The Villarreal Health Network",
          "description": "Contact information and office locations"
        },
        getBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'Contact', url: '/contact' }
        ])
      ]
    }
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    urgency: 'routine'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/request-callback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (result.success) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
          urgency: 'routine'
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactMethods = [
    {
      title: 'Emergency Care Line',
      description: 'Urgent care needs - immediate response',
      icon: 'ri-alarm-warning-line',
      color: 'red',
      contact: '(702) 555-STAT',
      subtext: 'Available 24/7 for emergencies',
      action: 'tel:+17025557828'
    },
    {
      title: 'Main Care Line',
      description: 'General inquiries and referrals',
      icon: 'ri-phone-line',
      color: 'blue',
      contact: '(702) 555-1234',
      subtext: 'Mon-Fri 8AM-6PM, Sat 9AM-2PM',
      action: 'tel:+17025551234'
    },
    {
      title: 'Email Support',
      description: 'Non-urgent questions and information',
      icon: 'ri-mail-line',
      color: 'green',
      contact: 'info@careconnectnevada.com',
      subtext: 'Response within 4 hours',
      action: 'mailto:info@careconnectnevada.com'
    },
    {
      title: 'Live Chat Support',
      description: 'Instant help with care coordination',
      icon: 'ri-customer-service-line',
      color: 'purple',
      contact: 'Chat Now',
      subtext: 'Available during business hours',
      action: () => document.querySelector('#vapi-widget-floating-button')?.click()
    }
  ];

  const locations = [
    {
      name: 'Las Vegas Office',
      address: '3755 Breakthrough Way, Las Vegas, NV 89135',
      phone: '(702) 555-1234',
      hours: 'Mon-Fri: 8AM-6PM, Sat: 9AM-2PM',
      services: 'Main coordination center for all agencies'
    },
    {
      name: 'Reno Office',
      address: '855 W Seventh St, Reno, NV 89503',
      phone: '(775) 555-1234',
      hours: 'Mon-Fri: 8AM-5PM',
      services: 'Northern Nevada care coordination'
    },
    {
      name: 'Henderson Office',
      address: '2620 Regatta Dr, Henderson, NV 89052',
      phone: '(702) 555-5678',
      hours: 'Mon-Fri: 8AM-6PM',
      services: 'Specialized dementia and memory care'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Enhanced Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-600 via-teal-600 to-blue-700 text-white py-24 pt-36 overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/9d34ccf55e363869aab80ea663c08603.jpg')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="mb-8">
              <span className="inline-block bg-white/20 text-white px-8 py-4 rounded-full text-xl font-semibold backdrop-blur-sm shadow-lg">
                We're Here to Help 24/7
              </span>
            </div>
            <h1 className="text-6xl md:text-7xl font-bold mb-8">
              Contact Our Care Team
            </h1>
            <p className="text-2xl text-blue-100 max-w-4xl mx-auto leading-relaxed">
              Get immediate assistance with your healthcare needs. Our experienced care coordinators are standing by to help you find the perfect solution.
            </p>
          </div>
        </div>
      </section>

      {/* Enhanced Contact Methods */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-bold text-gray-900 mb-8">
              Multiple Ways to Reach Us
            </h2>
            <p className="text-2xl text-gray-600">
              Choose the contact method that works best for you
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
            {contactMethods.map((method, index) => (
              <div key={index} className="bg-white rounded-3xl shadow-2xl p-12 text-center hover:shadow-3xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100">
                <div className={`w-24 h-24 bg-gradient-to-br from-${method.color}-500 to-${method.color}-600 rounded-3xl flex items-center justify-center mx-auto mb-10 shadow-xl`}>
                  <i className={`${method.icon} text-white text-4xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">{method.title}</h3>
                <p className="text-gray-600 mb-8 text-lg">{method.description}</p>
                <div className="mb-8">
                  {typeof method.action === 'function' ? (
                    <button
                      onClick={method.action}
                      className="text-blue-600 font-bold text-xl hover:text-blue-700 transition-colors duration-200 cursor-pointer"
                    >
                      {method.contact}
                    </button>
                  ) : (
                    <a
                      href={method.action}
                      className="text-blue-600 font-bold text-xl hover:text-blue-700 transition-colors duration-200"
                    >
                      {method.contact}
                    </a>
                  )}
                </div>
                <p className="text-gray-500 text-base">{method.subtext}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enhanced Contact Form */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-8">
              Request a Callback
            </h2>
            <p className="text-2xl text-gray-600">
              Fill out the form below and we'll contact you within 15 minutes during business hours
            </p>
          </div>

          {submitStatus === 'success' && (
            <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-8 mb-12">
              <div className="flex items-center">
                <i className="ri-check-circle-line text-green-600 text-3xl mr-6"></i>
                <div>
                  <h3 className="text-green-800 font-bold text-2xl">Request Submitted Successfully!</h3>
                  <p className="text-green-700 text-lg">Our care coordinator will contact you within 15 minutes during business hours.</p>
                </div>
              </div>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-8 mb-12">
              <div className="flex items-center">
                <i className="ri-error-warning-line text-red-600 text-3xl mr-6"></i>
                <div>
                  <h3 className="text-red-800 font-bold text-2xl">Submission Error</h3>
                  <p className="text-red-700 text-lg">Please try again or call us directly at (702) 555-1234.</p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="bg-white rounded-3xl shadow-2xl p-12 border border-gray-100">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <div>
                <label className="block font-bold text-gray-700 mb-4 text-lg">
                  Full Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <label className="block font-bold text-gray-700 mb-4 text-lg">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                  placeholder="(702) 555-0123"
                />
              </div>

              <div>
                <label className="block font-bold text-gray-700 mb-4 text-lg">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                  placeholder="your.email@example.com"
                />
              </div>

              <div>
                <label className="block font-bold text-gray-700 mb-4 text-lg">
                  Urgency Level *
                </label>
                <select
                  name="urgency"
                  value={formData.urgency}
                  onChange={handleInputChange}
                  required
                  className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg pr-8"
                >
                  <option value="routine">Routine (within 24 hours)</option>
                  <option value="urgent">Urgent (within 4 hours)</option>
                  <option value="stat">STAT (immediate)</option>
                </select>
              </div>
            </div>

            <div className="mb-8">
              <label className="block font-bold text-gray-700 mb-4 text-lg">
                Subject *
              </label>
              <input
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                required
                className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                placeholder="Brief description of your inquiry"
              />
            </div>

            <div className="mb-12">
              <label className="block font-bold text-gray-700 mb-4 text-lg">
                Message *
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                required
                rows={6}
                maxLength={500}
                className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                placeholder="Please describe your healthcare needs, questions, or how we can help you..."
              />
              <p className="text-gray-500 mt-3 text-base">
                {formData.message.length}/500 characters
              </p>
            </div>

            <div className="text-center">
              <Button
                type="submit"
                size="lg"
                disabled={isSubmitting}
                className="w-full md:w-auto whitespace-nowrap text-xl px-12 py-4"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line mr-3 animate-spin"></i>
                    Submitting Request...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line mr-3"></i>
                    Request Callback
                  </>
                )}
              </Button>
              
              <p className="text-gray-600 mt-6 text-lg">
                <i className="ri-time-line mr-2"></i>
                We'll respond within 15 minutes during business hours
              </p>
            </div>
          </form>
        </div>
      </section>

      {/* Enhanced Office Locations */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-bold text-gray-900 mb-8">
              Our Office Locations
            </h2>
            <p className="text-2xl text-gray-600">
              Serving families across Nevada with multiple convenient locations
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {locations.map((location, index) => (
              <div key={index} className="bg-white rounded-3xl shadow-2xl p-12 hover:shadow-3xl transition-shadow duration-300 border border-gray-100">
                <div className="text-center mb-10">
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-teal-500 rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-xl">
                    <i className="ri-map-pin-line text-white text-3xl"></i>
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-6">{location.name}</h3>
                </div>
                
                <div className="space-y-8">
                  <div className="flex items-start">
                    <i className="ri-map-pin-2-line text-blue-600 mr-4 mt-1 text-2xl"></i>
                    <p className="text-gray-700 text-lg">{location.address}</p>
                  </div>
                  
                  <div className="flex items-center">
                    <i className="ri-phone-line text-blue-600 mr-4 text-2xl"></i>
                    <a href={`tel:${location.phone}`} className="text-blue-600 font-semibold text-lg hover:text-blue-700 transition-colors duration-200">
                      {location.phone}
                    </a>
                  </div>
                  
                  <div className="flex items-start">
                    <i className="ri-time-line text-blue-600 mr-4 mt-1 text-2xl"></i>
                    <p className="text-gray-700 text-lg">{location.hours}</p>
                  </div>
                  
                  <div className="flex items-start">
                    <i className="ri-service-line text-blue-600 mr-4 mt-1 text-2xl"></i>
                    <p className="text-gray-700 text-lg">{location.services}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enhanced Map Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-8">
              Find Us on the Map
            </h2>
            <p className="text-2xl text-gray-600">
              Conveniently located throughout Nevada to serve you better
            </p>
          </div>
          
          <div className="rounded-3xl overflow-hidden shadow-2xl">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3218.5!2d-115.2!3d36.2!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzYuMjAwMDAwMCwtMTE1LjIwMDAwMDA!5e0!3m2!1sen!2sus!4v1234567890"
              width="100%"
              height="500"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="CareConnect Nevada Locations"
            />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
